/**
 * English-only pass-through stub.
 *
 * Background
 * ----------
 * A previous session prototyped a bilingual (EN + FR) UI. That effort was
 * paused and reverted for the launch, but a handful of components
 * (`Nav.jsx`, `BrandHead.jsx`, `App.js`) still import from this module.
 * Rewriting every call-site would be a large, risky diff on the eve of
 * launch — so this module is retained as a stub whose behaviour is
 * strictly English-only.
 *
 * Guarantees:
 *   • `FRENCH_PUBLIC_ENABLED` is permanently `false`, so any UI element
 *     gated by this flag (language switcher pills, /fr/* routes,
 *     hreflang alternates) is never rendered.
 *   • `useLang()` always reports `'en'`.
 *   • `buildLangPath` / `switchLangPath` are identity functions on the
 *     canonical (English) URL space.
 *   • `useT()` returns a plain fall-through translator: `t(key, fallback)`
 *     yields `fallback ?? key`, so consumers keep working even though
 *     no translation dictionary is wired up.
 */

import React, { createContext, useContext } from 'react';

export const FRENCH_PUBLIC_ENABLED = false;

const LanguageContext = createContext({ lang: 'en', setLang: () => {} });

export function LanguageProvider({ children }) {
    return (
        <LanguageContext.Provider value={{ lang: 'en', setLang: () => {} }}>
            {children}
        </LanguageContext.Provider>
    );
}

export function useLang() {
    return useContext(LanguageContext);
}

/** Identity: the site only serves English URLs. */
export function buildLangPath(path /*, _lang */) {
    return path;
}

/** Identity: no cross-language navigation is possible in English-only mode. */
export function switchLangPath(path /*, _lang */) {
    return path;
}

/**
 * English dictionary for keys that are still referenced in the UI after the
 * bilingual revert. Anything not listed here falls through to the caller's
 * `fallback` argument, or (last-resort) the key itself.
 */
const EN_STRINGS = {
    'nav.artists': 'Artists',
    'nav.artworks': 'Artworks',
    'nav.exhibitions': 'Exhibitions',
    'nav.about': 'About',
    'nav.journal': 'Journal',
    'nav.film': 'Film',
    'nav.contact': 'Contact',
    'nav.home_aria': 'Maison Touré — return to home',
    'nav.menu_aria': 'Open navigation menu',
};

export function useT() {
    return (key, fallback) => {
        if (Object.prototype.hasOwnProperty.call(EN_STRINGS, key)) {
            return EN_STRINGS[key];
        }
        return fallback === undefined ? key : fallback;
    };
}
